import { SamplingContext } from '@sentry/core';
import { BrowserOptions, browserTracingIntegration } from '@sentry/angular';
import { environment } from '@env/environment';

let throttleCount = 0;

type BeforeSendEvent = Parameters<NonNullable<BrowserOptions['beforeSend']>>[0];

const droppedSentryErrors: Array<{ error: string; browser?: string; version?: RegExp }> = [
  {
    error: "Cannot read properties of undefined (reading 'false')",
    browser: 'Chrome',
    version: /^126(\.|$)/
  }
];

export const shouldDropSentryEvent = (event: BeforeSendEvent) => {
  const error = event?.exception?.values?.[0]?.value;
  const browserContext = event.contexts?.browser;
  const browserName = browserContext?.name ?? (navigator.userAgent.includes('Chrome') ? 'Chrome' : '');
  const browserVersion = (typeof browserContext?.version === 'string' ? browserContext.version : null) ?? navigator.userAgent.match(/Chrome\/(\d+\.\S+)/)?.[1] ?? '';

  return droppedSentryErrors.some(rule => error === rule.error && (!rule.browser || browserName === rule.browser) && (!rule.version || rule.version.test(browserVersion)));
};

export const sentryConfig: BrowserOptions = {
  dsn: environment.sentryDsn,
  enabled: !!environment.sentryDsn && new RegExp(environment.validHostsRegExp, 'i').test(location.host),
  environment: environment.production ? 'production' : 'staging',
  release: environment.commitHash,
  integrations: [browserTracingIntegration({ instrumentPageLoad: false, enableLongTask: false, enableLongAnimationFrame: false })],
  maxBreadcrumbs: 20,
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 0,
  ignoreErrors: [
    'validation',
    'document_not_found',
    'signature_not_found',
    'link_not_found',
    'popup_closed_by_user',
    'document_signed',
    'unavailable_credits',
    'could_not_upload_file',
    'not_your_turn',
    'NetworkError',
    'Network error: undefined',
    'TypeError: Failed to fetch',
    'unavailable_verifications_credits',
    'user_biometric_verification_required',
    'token_invalid',
    'token_expired',
    'ApolloError: Unauthenticated',
    'unauthorized',
    'DataCloneError',
    '<unknown>',
    'Handled unknown error',
    'Too Many Attempts',
    'action_already_been_taken',
    'Error message not found',
    'queueMicrotask is not defined'
  ],
  beforeSend: event => {
    if (shouldDropSentryEvent(event)) {
      return null;
    }

    throttleCount++;
    return throttleCount > 5 ? null : event;
  },
  tracesSampler: (context: SamplingContext) => {
    if (
      context?.attributes &&
      context.attributes['sentry.op'] &&
      ['resource.script', 'resource.link', 'resource.css', 'resource.img', 'resource.icon', 'resource.other', 'measure'].includes(context.attributes['sentry.op'])
    ) {
      return 0;
    }
    return 0.1;
  }
};
